import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import dayjs from 'dayjs/esm';
import { Upload, UpdateUpload, BaseTag } from '@xmedical/core';
import { XmedicalUiModule } from '@xmedical/ui';
import { FileUploaderService } from '../../services/file-uploader.service';

export interface FileUpdaterDialogData {
  upload: Upload;
}

@Component({
  selector: 'xmed-file-updater',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    XmedicalUiModule
  ],
  providers: [],
  templateUrl: './file-updater.component.html',
  styleUrl: './file-updater.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUpdaterComponent implements OnInit {
  private readonly uploaderService = inject(FileUploaderService);
  private readonly bottomSheetData = inject<FileUpdaterDialogData>(MAT_BOTTOM_SHEET_DATA, { optional: true });
  private readonly dialogData = inject<FileUpdaterDialogData>(MAT_DIALOG_DATA, { optional: true });
  private readonly bottomSheetRef = inject<MatBottomSheetRef<FileUpdaterComponent>>(MatBottomSheetRef, { optional: true });
  private readonly dialogRef = inject<MatDialogRef<FileUpdaterComponent>>(MatDialogRef, { optional: true });

  processing = signal(false);
  upload: Upload | null = null;
  fileTags: BaseTag[] = [];
  documentDate: dayjs.Dayjs = dayjs();

  private get data(): FileUpdaterDialogData | null {
    return this.bottomSheetData || this.dialogData || null;
  }

  ngOnInit(): void {
    if (this.data?.upload) {
      this.upload = { ...this.data.upload };
      this.fileTags = this.upload.tags.map(tag => ({ tag: tag.tag, hexColor: tag.hexColor } as BaseTag));
      this.documentDate = dayjs(this.upload.documentDate);
    }
  }

  searchTags = (query: string): Observable<BaseTag[]> => {
    return this.uploaderService.queryTags(query);
  };

  onTagsChange(tags: BaseTag[]): void {
    this.fileTags = tags;
  }

  cancel(): void {
    this.close(null);
  }

  update(): void {
    if (!this.upload) return;

    this.processing.set(true);
    const updateData = this.generateUpload();

    this.uploaderService.updateUpload(this.upload.id, updateData).subscribe({
      next: (success) => {
        this.processing.set(false);
        if (success) {
          this.close({ ...this.upload!, ...updateData });
        }
      },
      error: (err) => {
        this.processing.set(false);
        console.error('Failed to update upload:', err);
      }
    });
  }

  private generateUpload(): UpdateUpload {
    return {
      id: this.upload!.id,
      tags: this.fileTags,
      fileName: this.upload!.fileName,
      documentDate: this.formatDateOnly(this.documentDate)
    };
  }

  private formatDateOnly(date: dayjs.Dayjs): string {
    return date.format('YYYY-MM-DD');
  }

  private close(result: Upload | null): void {
    if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(result);
    } else if (this.dialogRef) {
      this.dialogRef.close(result);
    }
  }
}
